import React, { useMemo } from 'react';
import './style.scss';
import { CardNames } from 'shared';
import { ColorSchema } from '../../../../../../../utils/color';
import SVGCardCross from '../../../../../../../assets/images/cards/cross';
import SVGCardRock from '../../../../../../../assets/images/cards/rock';
import SVGCardPaper from '../../../../../../../assets/images/cards/paper';
import SVGCardScissors from '../../../../../../../assets/images/cards/scissors';
import clsx from 'clsx';
import { SvgWithColorProps } from '../../../../../../../assets/images/interfaces';
import { useAssets } from '../../../../../../../contexts/assets';

export enum CardButtonStates {
  ACTIVE,
  PRESSED,
  DISABLED
}

interface Props {
  cardsAmount: number;
  type: CardNames;
  schema: ColorSchema;
  state: CardButtonStates;
  onClick?: (card: CardNames) => any;
  clickable: boolean;
}

const BattleActionCardButton = ({
  cardsAmount,
  type,
  state,
  schema,
  onClick,
  clickable
}: Props) => {
  const { sound } = useAssets();

  const buttonSchema = useMemo<ButtonSchema>(() => {
    return getColorsByState(schema, state);
  }, [schema, state]);

  const Icon = useMemo<React.FunctionComponent<SvgWithColorProps>>(() => {
    if (cardsAmount === 0) {
      return SVGCardCross;
    }
    switch (type) {
      case CardNames.ROCK:
        return SVGCardRock;
      case CardNames.PAPER:
        return SVGCardPaper;
      case CardNames.SCISSORS:
        return SVGCardScissors;
    }
  }, [type, cardsAmount]);

  const numberStyle = useMemo(
    () => ({
      boxShadow: `0 -3px 0 0 ${schema.shadows.dark}`,
      background: schema.backgrounds.dark,
      color: schema.text
    }),
    [schema]
  );

  const handleClick = () => {
    if (clickable && onClick) {
      onClick(type);
      sound.cardClick();
    }
  };

  return (
    <div className='fight-action-card-button-wrapper'>
      <button
        className={clsx(
          'fight-action-card-button',
          !clickable && 'no-animation',
          state === CardButtonStates.PRESSED && 'pressed'
        )}
        style={{
          background: buttonSchema.background,
          boxShadow: buttonSchema.shadow,
          color: buttonSchema.color
        }}
        onClick={handleClick}
      >
        <Icon color={buttonSchema.icon} />
      </button>
      <div className='fight-action-card-number' style={numberStyle}>
        {cardsAmount}
      </div>
    </div>
  );
};

type ButtonSchema = {
  background: string;
  icon: string;
  color?: string;
  shadow?: string;
};

const getColorsByState = (
  schema: ColorSchema,
  state: CardButtonStates
): ButtonSchema => {
  switch (state) {
    case CardButtonStates.ACTIVE: {
      return {
        background: schema.backgrounds.active,
        icon: schema.icons.active,
        color: schema.shadows.main
      };
    }
    case CardButtonStates.PRESSED: {
      return {
        background: schema.backgrounds.pressed,
        icon: schema.icons.pressed,
        color: schema.shadows.pressed
      };
    }
    case CardButtonStates.DISABLED:
      return {
        background: schema.backgrounds.disabled,
        icon: schema.backgrounds.dark,
        shadow: 'none'
      };
  }
};

export default BattleActionCardButton;
